import { DocumentStatus, Locale } from '@synergy/models';
import { Form, Formik, FormikProps } from 'formik';
import { FormattedMessage } from 'react-intl';
import { PaloDescriptionModeToggle, PaloFormSection } from '@synergy/components-palo';
import { ScrollToFieldError } from '@synergy/components';
import { Theme } from '@mui/material';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import { connect } from 'react-redux';
import { sortByProperty } from '@synergy/utils';

import { AppState } from '../../../../store/models/app-state';
import { DetailPositionsCtaHelper } from './components/detail-positions-ctaHelper';
import { DetailPositionsTable } from './components/detail-positions-table';
import {
    FormControlledStepComponent,
    FormControlledStepComponentProps,
} from '../../../../common/components/abstract/form-controlled-step-component';
import { PaloDirtyComparison } from '../../../../common/components/palo/palo-dirty-context';
import { PaloPurchaseOrderPositionTools } from '../../palo/palo-purchase-order-position-tools';
import { PaloPurchaseOrderPositionsWatcher } from '../../palo/palo-purchase-order-positions-watcher';
import { PurchaseOrder } from '../../models/purchaseOrder';
import {
    PurchaseOrderItem,
    isPurchaseOrderGenericItem,
    isPurchaseOrderProductItem,
} from '../../models/purchase-order-item';
import { PurchaseOrderProductItem } from '../../models/purchase-order-product-item';
import { bestLocaleSelector } from '../../../../common/selectors/locale-selectors';
import { pickCommonPartial } from '../../../../common/utils/pick-common-partial';
import { positionsValidationSchema } from './positions-validation-schema';
import { withFirstPosition, withoutBlankPositions } from '../../../../common/components/palo/palo-first-position';

const styles = (theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
        },
        content: {
            flex: 1,
            // A flex item refuses to shrink below its content by default, so
            // the wide position table pushed the whole mask past its column
            // and the totals ran under the attachments rail.
            minWidth: 0,
        },
        container: {
            // None of its own: the positions are drawn inside the head's form
            // now, which already keeps the edges every section on the mask has
            // (#2208).
            padding: 0,
        },
    });

interface PurchaseOrderDetailPositionsProps
    extends WithStyles<typeof styles>,
        FormControlledStepComponentProps<Partial<PurchaseOrder>> {
    defaultCurrency: string;
    locale: Locale;
    appLocale: Locale;
}

export interface PurchaseOrderDetailPositionsFormValues {
    currency?: string;
    allItems: PurchaseOrderItem[];
    netShippingCosts: number;
    netSurcharge: number;
    isTaxFree: boolean;
    tax: number;
}

class PurchaseOrderDetailPositionsBase extends FormControlledStepComponent<
    Partial<PurchaseOrder>,
    PurchaseOrderDetailPositionsFormValues,
    PurchaseOrderDetailPositionsProps
> {
    protected initialValues: PurchaseOrderDetailPositionsFormValues = {
        currency: this.props.defaultCurrency,
        allItems: [],
        netShippingCosts: 0,
        netSurcharge: 0,
        // A manual order is taxed like any other. The tax-free case comes from
        // an offer, which is recorded net, and arrives with the document
        // (#2210, R-10).
        isTaxFree: false,
        tax: 0,
    };

    protected onSubmit = (values: PurchaseOrderDetailPositionsFormValues) => {
        const newValues = pickCommonPartial(this.initialValues, values) as PurchaseOrderDetailPositionsFormValues;

        // A blank row is no position and is not handed on (#2308).
        const kept = withoutBlankPositions(values.allItems, isPurchaseOrderProductItem);
        const purchaseOrderItems = kept.filter(isPurchaseOrderProductItem);
        const surchargeItems = kept.filter(isPurchaseOrderGenericItem);

        const { allItems: _, ...internalState } = newValues;

        this.props.propagateInternalState({
            ...internalState,
            purchaseOrderItems,
            surchargeItems,
        });
        if (this.formikInstance) {
            this.formikInstance.resetForm({
                values: {
                    ...newValues,
                },
            });
        }
    };

    /** The blank row "Zeile hinzufügen" adds, as the first position of an order without one. */
    private blankItem = (): PurchaseOrderItem =>
        PurchaseOrderProductItem.fromPlainObject({
            name: {},
            quantity: 1,
            price: 0,
            discount: 0,
            tax: 0,
            orderUnit: '',
            isBestPrice: false,
            currency: this.props.globalState?.currency ?? this.props.defaultCurrency,
            position: 1,
        });

    private isReadonly(): boolean {
        const status = this.props.globalState?.status;

        return !this.props.unlocked && !!status && status !== DocumentStatus.Created;
    }

    private toFormValues(state?: Partial<PurchaseOrder>): PurchaseOrderDetailPositionsFormValues {
        // Allow all properties of the purchaseOrder partial, unused ones are ignored
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const definedState: any = {};

        Object.keys(state ?? {})
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .filter(key => (state as any)[key] != null)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .forEach(key => (definedState[key] = (state as any)[key]));

        const allItems = sortByProperty({
            data: [...(definedState.purchaseOrderItems || []), ...(definedState.surchargeItems || [])],
            key: 'position',
        });

        // If there are items without position or with duplicated positions, re-assign positions sequentially
        let lastPosition = 0;
        allItems.forEach(item => {
            if (!item.position || item.position <= lastPosition) {
                item.position = lastPosition + 1;
            }
            lastPosition = item.position;
        });

        // Reduced to the fields of this step, the same way onSubmit hands them
        // back. Otherwise the document's extra fields would make every
        // comparison against it fail.
        //
        // The first position stands there while the order has none. The form
        // and the document compared against both pass through here, so the
        // blank row is no unsaved input (#2308).
        return {
            ...this.initialValues,
            ...pickCommonPartial(this.initialValues, definedState),
            allItems: withFirstPosition(allItems, !this.isReadonly(), this.blankItem),
        };
    }

    private updateInternalValuesByProps(props: PurchaseOrderDetailPositionsProps) {
        if (!this.formikInstance) {
            return;
        }

        // A new order has no state of its own yet and still opens with its
        // first position (#2308).
        this.formikInstance.resetForm({ values: this.toFormValues(props.internalState) });
    }

    public componentDidMount() {
        this.updateInternalValuesByProps(this.props);
    }

    public UNSAFE_componentWillReceiveProps(nextProps: PurchaseOrderDetailPositionsProps) {
        if (nextProps.internalState !== this.props.internalState) {
            this.updateInternalValuesByProps(nextProps);
        }
    }

    public render() {
        const { classes, defaultCurrency, locale, appLocale, onCtaButtonChanged, unlocked } = this.props;

        const readonly = this.isReadonly();

        const supplier = this.props.globalState?.supplierAccount;
        const currency = this.props.globalState?.currency ?? defaultCurrency;

        return (
            <div className={classes.root}>
                <div className={classes.content}>
                    <div className={classes.container}>
                        <Formik
                            initialValues={this.initialValues}
                            onSubmit={this.onSubmit}
                            innerRef={ref => (this.formikInstance = ref)}
                            validationSchema={positionsValidationSchema}
                        >
                            {(formikProps: FormikProps<PurchaseOrderDetailPositionsFormValues>) => {
                                return (
                                    <Form>
                                        <PaloDirtyComparison baseline={this.toFormValues(this.props.documentState)} />
                                        <PaloPurchaseOrderPositionsWatcher />
                                        <ScrollToFieldError />
                                        {/* The mask's own section, so the
                                            positions keep the heading and the
                                            spacing of every other block on it
                                            (#2208). */}
                                        <PaloFormSection
                                            title={<FormattedMessage id="app.header.positions" />}
                                            action={<PaloDescriptionModeToggle />}
                                        >
                                            <DetailPositionsTable
                                                toolbar={
                                                    <PaloPurchaseOrderPositionTools
                                                        entity={this.props.globalState}
                                                        unlocked={!!this.props.unlocked}
                                                    />
                                                }
                                                purchaseOrderItems={formikProps.values.allItems}
                                                supplier={supplier}
                                                currency={currency}
                                                locale={locale}
                                                appLocale={appLocale}
                                                readonly={readonly}
                                            />
                                        </PaloFormSection>
                                        <DetailPositionsCtaHelper
                                            onChange={onCtaButtonChanged}
                                            formikProps={formikProps}
                                        />
                                    </Form>
                                );
                            }}
                        </Formik>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: AppState) => ({
    locale: bestLocaleSelector(state),
    appLocale: state.currentLocale,
    defaultCurrency: state.settings.currency,
});

const ConnectedPurchaseOrderDetailPositions = connect(mapStateToProps, {})(PurchaseOrderDetailPositionsBase);

export const PurchaseOrderDetailPositions = withStyles(styles)(ConnectedPurchaseOrderDetailPositions);
